import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCalendar, FaClock, FaMapMarkerAlt } from 'react-icons/fa';

// Animations
const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled Components
const EventsSection = styled.section`
  padding: 60px 20px;
  background-color: #ffffff;
  color: #1a1a1a;

  @media (max-width: 768px) {
    flex-direction: row;
   
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
  animation: ${fadeUp} 0.6s ease-out;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 16px;
  color: #cc0000;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
`;

const EventGrid = styled.div`
  display: flex;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  padding: 0 20px;

  @media (max-width: 768px) {
    display: grid;
    justify-content: center;
  }
`;

const EventCard = styled.article`
  border: 2px solid;
  border-color: ${props => {
    switch (props.category) {
      case 'music':
        return 'blue';
      case 'art':
        return 'green';
      default:
        return '#cc0000';
    }
  }};
  border-radius: 12px;
  padding: 24px;
  background-color: ${props => {
    switch (props.category) {
      case 'service': return '#ffffff';
      case 'outreach': return '#ffffff';
      case 'study': return '#ffffff';
      default: return 'white';
    }
  }};
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out forwards;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const EventTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
  transition: color 0.3s ease;
  
  ${EventCard}:hover & {
    color: ${props => {
      switch (props.color) {
        case 'service': return '#cc0000';
        case 'outreach': return '#cc0000';
        case 'study': return '#cc0000';
        default: return '#4B5563';
      }
    }};
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #4B5563;
  
  svg {
    color: #6B7280;
  }
`;

const EventDetails = styled.p`
  color: #4B5563;
  font-size: 0.9375rem;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E5E7EB;
`;

const LearnMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  color: #6B7280;
  font-weight: 500;
  margin-top: 20px;
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #374151;
    
    svg {
      transform: translateX(4px);
    }
  }
  
  svg {
    margin-left: 8px;
    transition: transform 0.3s ease;
  }
`;

interface Event {
  title: string;
  date: string;
  time?: string;
  location?: string;
  details: string;
  category?: 'service' | 'outreach' | 'study';
}

const Events = () => {
  const eventsData: Event[] = [
    {
      title: 'Sunday Service',
      date: 'Sunday, April 28, 2025',
      time: '10:00 AM',
      location: 'Main Hall',
      details: 'Join us for our weekly service at 10:00 AM.',
      category: 'service'
    },
    {
      title: 'Community Outreach',
      date: 'Saturday, May 4, 2025',
      time: '9:00 AM',
      location: 'Community Center',
      details: 'Help us serve the local community with our outreach program.',
      category: 'outreach'
    },
    {
      title: 'Bible Study',
      date: 'Wednesday, May 8, 2025',
      time: '7:00 PM',
      location: 'Fellowship Hall',
      details: 'Weekly Bible study at 7:00 PM in the fellowship hall.',
      category: 'study'
    }
  ];

  return (
    <EventsSection id='events'>
      <Container>
        <Header>
          <Title>Upcoming Events</Title>
          <Subtitle>
            Join us for these upcoming events and be part of our community.
          </Subtitle>
        </Header>
        
        <EventGrid>
          {eventsData.map((event, index) => (
            <EventCard 
            key={index} 
            category={event.category} 
            style={{ animationDelay: `${index * 100}ms` }}
          >
          
              <EventTitle color={event.category}>{event.title}</EventTitle>
              
              <DetailItem>
                <FaCalendar />
                <span>{event.date}</span>
              </DetailItem>
              
              {event.time && (
                <DetailItem>
                  <FaClock />
                  <span>{event.time}</span>
                </DetailItem>
              )}
              
              {event.location && (
                <DetailItem>
                  <FaMapMarkerAlt />
                  <span>{event.location}</span>
                </DetailItem>
              )}
              
              <EventDetails>{event.details}</EventDetails>
              
              <LearnMoreButton>
                Learn more
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </LearnMoreButton>
            </EventCard>
          ))}
        </EventGrid>
      </Container>
    </EventsSection>
  );
};

export default Events;